import React from "react";
import Text from "@/components/ui/text";
import { RadioGroupItem } from "@/components/ui/radio-group";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import { cn } from "@/utils/utils";

interface Props {
  value: string;
  title: string;
  description: string;
  footnote: string;
  icon: React.ReactNode;
  // Passed in rather than derived, so the card stays presentational.
  iconBgColor: string;
  // Indexes the shared milestone palette LogoIconContainer draws its frame
  // gradient from. Not a Tailwind ring-* utility, which is a box-shadow.
  iconPaletteIndex: number;
  isSelected: boolean;
  onSelect: () => void;
  /** Card-level note about the contract's current state, below the footnote. */
  notice?: React.ReactNode;
  /** Refuses selection. The notice is what says why, so it stays undimmed. */
  isDisabled?: boolean;
  "data-test"?: string;
}

const UpdateTermsKindCard: React.FC<Props> = ({
  value,
  title,
  description,
  footnote,
  icon,
  iconBgColor,
  iconPaletteIndex,
  isSelected,
  onSelect,
  notice,
  isDisabled = false,
  "data-test": dataTest
}) => (
  <label
    htmlFor={`update-terms-kind-${value}`}
    className={cn(
      "flex flex-1 min-w-0 flex-col gap-6 cursor-pointer rounded-2xl bg-surface-field-default px-4 pt-4 pb-6 transition-colors duration-200 ease-in-out hover:bg-surface-hover-soft",
      isSelected
        ? "border border-border-selected-card"
        : "border-[0.5px] border-border-unselected-card",
      // tailwind-merge drops the hover fill above, so the card stays inert
      // under the cursor rather than inviting a click it will not accept.
      isDisabled && "cursor-not-allowed hover:bg-surface-field-default"
    )}
    data-disabled={isDisabled}
    data-test={dataTest}
  >
    <div className="flex items-start justify-between w-full">
      {/* The tile dims; the radio is left alone because RadioGroupItem already
          carries its own `disabled:opacity-50`, and nesting it inside another
          opacity would multiply the two to ~0.3. */}
      <LogoIconContainer
        index={iconPaletteIndex}
        bgColor={iconBgColor}
        icon={icon}
        className={cn("rounded-lg", isDisabled && "opacity-60")}
        innerClassName="rounded-lg"
      />
      <div className="flex items-center p-2">
        <RadioGroupItem
          id={`update-terms-kind-${value}`}
          value={value}
          disabled={isDisabled}
          onClick={isDisabled ? undefined : onSelect}
          // The shared radio defaults to CTA green; these cards use the card border's neutral.
          className={cn(
            "border-border-border1 text-h1",
            isSelected && "border-border-selected-card"
          )}
        />
      </div>
    </div>
    <div className="flex flex-col gap-2 w-full">
      <div className={cn("flex flex-col gap-2", isDisabled && "opacity-60")}>
        <Text variant="smSubtitle">{title}</Text>
        <Text variant="body">{description}</Text>
        <div className="py-2 w-full">
          <div className="h-px w-full bg-border-unselected-card" />
        </div>
        <Text variant="bodySecondary">{footnote}</Text>
      </div>
      {notice ? <div className="pt-2">{notice}</div> : null}
    </div>
  </label>
);

export default UpdateTermsKindCard;
